
import type { PowerPstyleDialogMock, PowerPstyleThemeBackground } from '@/lib/power-pstyle-themes';
import {
  PSTYLE_DIALOG_FRIEND_STATUS,
  PSTYLE_DIALOG_ONLINE_STATUS,
  PSTYLE_DIALOG_USER_ID,
  pstyleAnimatedGradientStyle,
} from '@/lib/power-pstyle-themes';
import { PSTYLE_GIFT_ICON, PSTYLE_PROFILE_ACTIONS } from '@/lib/pstyle-button-icons';
import { GsImage } from '@/components/help-center/latest-power-shared';
import { cn } from '@/lib/utils';

/** Largura típica do user-dialog do xat (px). */
export const XAT_PROFILE_DIALOG_WIDTH = 400;

/** Stack tipográfica do user-dialog do xat. */
const XAT_USER_DIALOG_FONT =
  '-apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';

const SPIDERWEB_SVG = `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='36' viewBox='0 0 120 36'>
  <g stroke='rgba(173,216,255,0.28)' stroke-width='0.45' fill='none'>
    <line x1='60' y1='18' x2='0' y2='0'/><line x1='60' y1='18' x2='120' y2='0'/>
    <line x1='60' y1='18' x2='0' y2='18'/><line x1='60' y1='18' x2='120' y2='18'/>
    <line x1='60' y1='18' x2='0' y2='36'/><line x1='60' y1='18' x2='120' y2='36'/>
    <line x1='60' y1='18' x2='60' y2='0'/><line x1='60' y1='18' x2='60' y2='36'/>
    <line x1='60' y1='18' x2='30' y2='0'/><line x1='60' y1='18' x2='90' y2='0'/>
    <line x1='60' y1='18' x2='30' y2='36'/><line x1='60' y1='18' x2='90' y2='36'/>
    <circle cx='60' cy='18' r='6'/><circle cx='60' cy='18' r='12'/><circle cx='60' cy='18' r='18'/>
    <path d='M42 6 Q60 14 78 6'/><path d='M42 30 Q60 22 78 30'/>
    <path d='M24 18 Q60 8 96 18'/><path d='M24 18 Q60 28 96 18'/>
  </g>
</svg>`;

function buttonBackground(mock: PowerPstyleDialogMock) {
  if (mock.animatedGradient) {
    return mock.animatedGradient;
  }
  const gradient = mock.buttonGradient;
  if (mock.buttonPattern === 'spiderweb') {
    const web = `url("data:image/svg+xml,${encodeURIComponent(SPIDERWEB_SVG)}")`;
    return `${web} center / 120px 36px repeat, ${gradient}`;
  }
  return gradient;
}

function headerBarStyle(mock: PowerPstyleDialogMock): React.CSSProperties {
  if (mock.animatedGradient) {
    return {
      ...pstyleAnimatedGradientStyle(mock.animatedGradient),
      color: mock.headerTextColor ?? '#ffffff',
    };
  }
  return {
    background: mock.headerGradient,
    color: mock.headerTextColor ?? '#ffffff',
  };
}

function actionButtonStyle(mock: PowerPstyleDialogMock): React.CSSProperties {
  const theme = mock.themeBackground;
  if (mock.animatedGradient) {
    return pstyleAnimatedGradientStyle(mock.animatedGradient);
  }
  if (theme) {
    return { background: theme.gradient };
  }
  return { background: buttonBackground(mock) };
}

const HEADER_ICON_BTN =
  'flex h-[22px] w-[22px] items-center justify-center rounded-[5px] border-0 bg-transparent p-0 leading-none text-white opacity-90 transition-[background-color,opacity] duration-150 ease-out hover:bg-white/20 hover:opacity-100';

const ACTION_BTN =
  'group relative flex min-h-[36px] cursor-pointer items-center justify-center overflow-hidden rounded-[6px] border-0 px-2 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] transition-[filter,box-shadow] duration-150 ease-out hover:brightness-110 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_1px_3px_rgba(0,0,0,0.18)] active:brightness-95';

/** Camada `:before` do xat — imagem sobre o degradê (só nos botões). */
function PstyleThemeOverlay({
  theme,
  className,
}: {
  theme: PowerPstyleThemeBackground;
  className?: string;
}) {
  return (
    <span
      className={cn('pointer-events-none absolute inset-0 bg-no-repeat', className)}
      style={{
        backgroundImage: `url(${theme.overlayImageUrl})`,
        backgroundSize: theme.overlayBackgroundSize ?? 'cover',
        backgroundPosition: theme.overlayBackgroundPosition ?? '50% 50%',
        opacity: theme.overlayOpacity ?? 0.35,
      }}
      aria-hidden
    />
  );
}

function ThemedActionButton({
  mock,
  label,
  iconSrc,
}: {
  mock: PowerPstyleDialogMock;
  label: string;
  iconSrc: string;
}) {
  const theme = mock.themeBackground;
  const buttonTextColor = mock.buttonTextColor ?? '#ffffff';

  return (
    <button
      type="button"
      className={ACTION_BTN}
      style={{
        ...actionButtonStyle(mock),
        ...(mock.buttonBoxShadow ? { boxShadow: mock.buttonBoxShadow } : undefined),
      }}
      aria-label={label}
    >
      {theme ? <PstyleThemeOverlay theme={theme} className="rounded-[6px]" /> : null}
      <span
        className="pointer-events-none absolute inset-0 z-[2] rounded-[6px] bg-white/0 transition-colors duration-150 ease-out group-hover:bg-white/10"
        aria-hidden
      />
      <img
        src={iconSrc}
        alt=""
        className="absolute left-2 top-1/2 z-[3] h-5 w-5 -translate-y-1/2 object-contain"
        draggable={false}
        aria-hidden
      />
      <span
        className="relative z-[3] px-5 text-center text-[12px] font-bold leading-tight"
        style={{ color: buttonTextColor }}
      >
        {label}
      </span>
    </button>
  );
}

function HeaderBlockIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={cn('h-[15px] w-[15px]', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden
    >
      <circle cx="8" cy="8" r="6.25" />
      <path d="M3.6 12.4L12.4 3.6" strokeLinecap="round" />
    </svg>
  );
}

function HeaderCloseIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={cn('h-[16px] w-[16px]', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      aria-hidden
    >
      <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
    </svg>
  );
}

export default function PowerProfileDialogMock({
  mock,
  className,
}: {
  mock: PowerPstyleDialogMock;
  className?: string;
}) {
  const userId = mock.userId ?? PSTYLE_DIALOG_USER_ID;
  const onlineStatus = mock.onlineStatus ?? PSTYLE_DIALOG_ONLINE_STATUS;
  const friendStatus = mock.friendStatus ?? PSTYLE_DIALOG_FRIEND_STATUS;

  const headerContent = (
    <>
      <span className="truncate text-[14px] font-bold leading-none">
        {mock.displayName} ({userId})
      </span>
      <div className="flex shrink-0 items-center gap-0.5 pl-2">
        <button type="button" className={HEADER_ICON_BTN} aria-label="Ignorar utilizador">
          <HeaderBlockIcon />
        </button>
        <button type="button" className={HEADER_ICON_BTN} aria-label="Fechar diálogo">
          <HeaderCloseIcon />
        </button>
      </div>
    </>
  );

  return (
    <div
      className={cn(
        'mx-auto w-full overflow-hidden rounded-[14px] bg-white shadow-[0_4px_24px_rgba(43,46,131,0.22)]',
        className
      )}
      style={{
        backgroundColor: '#ffffff',
        fontFamily: XAT_USER_DIALOG_FONT,
        maxWidth: XAT_PROFILE_DIALOG_WIDTH,
      }}
    >
      <div
        className="flex items-center justify-between px-3 py-[9px]"
        style={headerBarStyle(mock)}
      >
        {headerContent}
      </div>

      <div className="px-3 pb-3 pt-2.5" style={{ backgroundColor: mock.bodyBackground }}>
        <div className="mb-2 flex gap-2.5">
          <div className="flex h-[74px] w-[74px] shrink-0 items-center justify-center">
            <GsImage
              code={mock.avatarCode}
              sizePx={40}
              className="flex h-[68px] w-[68px] items-center justify-center"
              imgClassName="h-[68px] w-[68px] object-contain"
            />
          </div>

          <div className="flex min-w-0 flex-col items-start gap-[3px] pt-0.5">
            <div
              className="w-fit max-w-full rounded-[5px] px-2 py-[3px] text-[12px] font-bold leading-none"
              style={{
                backgroundColor: mock.infoBoxBackground,
                color: mock.infoBoxTextColor,
                boxShadow: mock.infoBoxShadow,
              }}
            >
              {mock.displayName}
            </div>
            <div
              className="w-fit max-w-full rounded-[5px] px-2 py-[4px] text-[11px] leading-[1.2]"
              style={{
                backgroundColor: mock.infoBoxBackground,
                color: mock.infoBoxTextColor,
                boxShadow: mock.infoBoxShadow,
              }}
            >
              {onlineStatus}
              <br />
              {friendStatus}
            </div>
            <div className="pt-0.5 leading-none" aria-hidden>
              <img
                src={PSTYLE_GIFT_ICON}
                alt=""
                className="h-[16px] w-[16px] object-contain"
                draggable={false}
              />
            </div>
          </div>
        </div>

        <div
          className="mb-2.5 rounded-[6px] border px-2 py-[5px] text-center"
          style={{
            backgroundColor: mock.statusBarBackground,
            borderColor: mock.statusBarBorder,
          }}
        >
          <span
            className="text-[13px] font-bold leading-none"
            style={{ color: mock.statusTextColor }}
          >
            {mock.statusText}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-[6px]">
          {PSTYLE_PROFILE_ACTIONS.map(({ label, icon }) => (
            <ThemedActionButton
              key={label}
              mock={mock}
              label={label}
              iconSrc={icon}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
